import React from 'react';
import {List} from 'immutable';
import {UserContextViewPlusButton} from './UserContextView';
import SearchFieldStore from 'stores/triggers/SearchFieldStore';
import connectToStores from 'alt-utils/lib/connectToStores';
import SearchFieldActions from 'actions/triggers/SearchFieldActions';
import Button from '@material-ui/core/Button';
import CrmSelectionViewPlusButton from './crm-selection/CrmSelectionView';
import isNil from 'lodash/isNil';
import SearchFieldSelectionViewPlusButton from './search-field-selection/SearchFieldSelectionView';
import CallTrigger, {
  CallConditionAll,
  CallConditionAny,
  CallConditionType,
  CallConditionUser,
} from 'records/CallTrigger';
import CallConversationSelectionViewPlusButton from 'components/triggers/call-conversation-selection/CallConversationSelectionView';
import KeywordTopicSelectionViewPlusButton from 'components/triggers/keyword-topic-selection/KeywordTopicSelectionViewPlusButton';
import './CallRuleStyles.styl';
import TopicCoverageSelectionViewPlusButton from 'components/triggers/topic-coverage/TopicCoverageSelectionViewPlusButton';
import AccountActions from 'actions/AccountActions';
import AccountStore from 'stores/AccountStore';
import pick from 'lodash/pick';
import {UserContextType} from 'components/triggers/UserContextType';

@connectToStores
export default class CallRuleWizard extends React.Component {
  constructor(props) {
    super(props);

    this.crmOption = this.crmOption.bind(this);
    this.removeUserContext = this.removeUserContext.bind(this);
    this.updateUserContext = this.updateUserContext.bind(this);
    this.addUserContext = this.addUserContext.bind(this);
    this.updateItem = this.updateItem.bind(this);
    this.addItem = this.addItem.bind(this);
    this.removeItem = this.removeItem.bind(this);
    this.removeItemPath = this.removeItemPath.bind(this);
    this.searchFieldOption = this.searchFieldOption.bind(this);
    this.updateItemPath = this.updateItemPath.bind(this);
    this.userOption = this.userOption.bind(this);
    this.getOption = this.getOption.bind(this);
    this.conversationOption = this.conversationOption.bind(this);
    this.keywordOption = this.keywordOption.bind(this);
    this.topicCoverageOption = this.topicCoverageOption.bind(this);
    this.saveTriggerClick = this.saveTriggerClick.bind(this);

    this.state = {
      conditions: List(),
      saving: false,
    };
  }

  static getStores() {
    return [SearchFieldStore, AccountStore];
  }

  static getPropsFromStores() {
    const searchFieldStore = SearchFieldStore.getState();
    return {
      searchFields: searchFieldStore.searchFields,
      searchFieldsLoading: searchFieldStore.searchFieldsLoading,
      searchFieldsLoaded: searchFieldStore.searchFieldsLoaded,
      ...pick(AccountStore.getState(), [
        'hasSalesforce',
        'hasSalesforceLoading',
        'hasSalesforceLoaded',
      ]),
    };
  }

  componentDidMount() {
    const {rule} = this.props;
    if (rule && rule.conditions) {
      this.setState({
        conditions: rule.conditions.matchAll,
      });
    }
    if (!this.props.searchFieldsLoaded) {
      SearchFieldActions.fetchSearchFields.defer();
    }

    if (!this.props.hasSalesforceLoaded && !this.props.hasSalesforceLoading) {
      AccountActions.accountHasSalesforce.defer();
    }
  }

  /* doc me */
  getUserConditionPath(index) {
    const {conditions} = this.state;
    const ucIndex = this.getUserConditionIndex(conditions);
    return [ucIndex, 'matchAny', index];
  }

  addUserContext(selectedOption) {
    const {conditions} = this.state;
    this.addItem(
      this.getUserConditionIndex(conditions),
      new CallConditionUser({userCondition: selectedOption})
    );
  }

  /* doc me */
  removeUserContext(path) {
    this.removeItemPath(path);
  }

  /* doc me */
  updateUserContext(path, context) {
    const userCondition = new CallConditionUser({userCondition: context});
    this.updateItemPath(path, userCondition);
  }

  /**
   * remove call condition in matchAny by parent index and child index
   * @param index parent index
   * @param itemIndex child index
   */
  removeItem(index, itemIndex) {
    const {conditions} = this.state;
    const removeCondition = conditions.getIn([index, 'matchAny']).size === 1;
    this.setState({
      conditions: removeCondition
        ? conditions.delete(index)
        : conditions.deleteIn([index, 'matchAny', itemIndex]),
    });
  }

  /**
   * update call condition in matchAny by parent index and child index
   * @param index parent index
   * @param itemIndex child index
   * @param item
   */
  updateItem(index, itemIndex, item) {
    this.setState({
      conditions: this.state.conditions.updateIn([index, 'matchAny'], matchAny =>
        matchAny.set(itemIndex, item)
      ),
    });
  }

  /**
   *  remove call condition by path, and remove parent if empty
   *  @param path of item in conditions
   */
  removeItemPath(path) {
    const {conditions} = this.state;
    const parent = path.slice(0, -1);
    const removeParent = conditions.getIn(parent).size === 1;
    this.setState({
      conditions: removeParent
        ? conditions.deleteIn(parent.slice(0, -1))
        : conditions.deleteIn(path),
    });
  }

  /**
   *
   * @param path
   * @param item
   */
  updateItemPath(path, item) {
    this.setState({
      conditions: this.state.conditions.setIn(path, item),
    });
  }

  addItem(index, item) {
    const {conditions} = this.state;
    const condition = conditions.get(index);
    const newConditions =
      !condition || index === -1
        ? conditions.push(new CallConditionAny({matchAny: List([item])}))
        : conditions.updateIn([index, 'matchAny'], matchAny => matchAny.push(item));
    this.setState({
      conditions: newConditions,
    });
  }

  userOption(userContext, index) {
    const userConditions = (userContext || List()).map(c => c.userCondition);
    const contextPathMap = userConditions.map((context, i) => [
      this.getUserConditionPath(i),
      context,
    ]);
    return (
      <UserContextViewPlusButton
        key={index}
        contextPathMap={contextPathMap}
        removeItem={this.removeUserContext}
        updateItem={this.updateUserContext}
        addItem={this.addUserContext}
        buttonLabel="has a specific caller"
        contextType={UserContextType.CALLER}
        onSelect={selected => this.setState({selectedIndex: selected ? index : null})}
      />
    );
  }

  crmOption(matchAny, index) {
    return (
      <CrmSelectionViewPlusButton
        key={index}
        matchAny={matchAny}
        removeItem={itemIndex => this.removeItem(index, itemIndex)}
        updateItem={(itemIndex, item) => this.updateItem(index, itemIndex, item)}
        addItem={item => this.addItem(index, item)}
        onSelect={selected => this.setState({selectedIndex: selected ? index : null})}
      />
    );
  }

  searchFieldOption(matchAny, index) {
    const {searchFieldsLoading, searchFields} = this.props;
    return (
      !searchFieldsLoading &&
      searchFields.size > 0 && (
        <SearchFieldSelectionViewPlusButton
          key={index}
          matchAny={matchAny}
          removeItem={itemIndex => this.removeItem(index, itemIndex)}
          updateItem={(itemIndex, item) => this.updateItem(index, itemIndex, item)}
          addItem={item => this.addItem(index, item)}
          onSelect={selected => this.setState({selectedIndex: selected ? index : null})}
        />
      )
    );
  }

  conversationOption(matchAny, index) {
    return (
      <CallConversationSelectionViewPlusButton
        key={index}
        matchAny={matchAny}
        removeItem={itemIndex => this.removeItem(index, itemIndex)}
        updateItem={(itemIndex, item) => this.updateItem(index, itemIndex, item)}
        addItem={item => this.addItem(index, item)}
        onSelect={selected => this.setState({selectedIndex: selected ? index : null})}
      />
    );
  }

  keywordOption(matchAny, index) {
    return (
      <KeywordTopicSelectionViewPlusButton
        key={index}
        matchAny={matchAny}
        removeItem={itemIndex => this.removeItem(index, itemIndex)}
        updateItem={(itemIndex, item) => this.updateItem(index, itemIndex, item)}
        addItem={item => this.addItem(index, item)}
        onSelect={selected => this.setState({selectedIndex: selected ? index : null})}
      />
    );
  }

  topicCoverageOption(matchAny, index) {
    return (
      <TopicCoverageSelectionViewPlusButton
        key={index}
        matchAny={matchAny}
        removeItem={itemIndex => this.removeItem(index, itemIndex)}
        updateItem={(itemIndex, item) => this.updateItem(index, itemIndex, item)}
        addItem={item => this.addItem(index, item)}
        onSelect={selected => this.setState({selectedIndex: selected ? index : null})}
      />
    );
  }

  isUserCondition(c) {
    return (
      c.type === CallConditionType.MATCH_ANY &&
      c.matchAny.size > 0 &&
      c.matchAny.get(0).type === CallConditionType.USER
    );
  }

  isCrmCondition(c) {
    return (
      c.type === CallConditionType.MATCH_ANY &&
      c.matchAny.size > 0 &&
      CallConditionType.isCrmCondition(c.matchAny.get(0).type)
    );
  }

  isSearchCondition(c) {
    return (
      c.type === CallConditionType.MATCH_ANY &&
      c.matchAny.size > 0 &&
      CallConditionType.isFieldCondition(c.matchAny.get(0).type)
    );
  }

  isConversationCondition(c) {
    return (
      c.type === CallConditionType.MATCH_ANY &&
      c.matchAny.size > 0 &&
      CallConditionType.isConversationCondition(c.matchAny.get(0).type)
    );
  }

  isKeywordCondition(c) {
    return (
      c.type === CallConditionType.MATCH_ANY &&
      c.matchAny.size > 0 &&
      CallConditionType.isKeywordCondition(c.matchAny.get(0).type)
    );
  }

  isTopicCoverageCondition(c) {
    return (
      c.type === CallConditionType.MATCH_ANY &&
      c.matchAny.size > 0 &&
      CallConditionType.isTopicCoverageCondition(c.matchAny.get(0).type)
    );
  }

  getUserCondition(conditions) {
    return conditions.find(c => this.isUserCondition(c));
  }

  getUserConditionIndex(conditions) {
    const condition = this.getUserCondition(conditions);
    return conditions.indexOf(condition);
  }

  getOption(item, index) {
    if (this.isUserCondition(item)) {
      return this.userOption(item.matchAny, index);
    }
    if (this.isCrmCondition(item)) {
      return this.crmOption(item.matchAny, index);
    }
    if (this.isConversationCondition(item)) {
      return this.conversationOption(item.matchAny, index);
    }
    if (this.isSearchCondition(item)) {
      return this.searchFieldOption(item.matchAny, index);
    }
    if (this.isKeywordCondition(item)) {
      return this.keywordOption(item.matchAny, index);
    }
    if (this.isTopicCoverageCondition(item)) {
      return this.topicCoverageOption(item.matchAny, index);
    }
  }

  getInitialOptions() {
    const options = [this.userOption, this.searchFieldOption, this.crmOption];
    return <div className="initial-options">{this.renderOptions(options)}</div>;
  }

  getContinuingOptions(includeUser) {
    const options = [
      this.searchFieldOption,
      this.crmOption,
      this.conversationOption,
      this.keywordOption,
      this.topicCoverageOption,
    ];
    if (includeUser) {
      options.unshift(this.userOption);
    }
    return <div className="continuing-options">{this.renderOptions(options)}</div>;
  }

  needContinuingOptions() {
    const {selectedIndex, conditions} = this.state;
    return isNil(selectedIndex) || selectedIndex >= conditions.size;
  }

  renderOptions(options) {
    const {selectedIndex, conditions} = this.state;
    const conditionsSize = conditions.size;
    return options.map((option, i) => {
      const index = conditionsSize + i;
      return isNil(selectedIndex) || selectedIndex === index ? option(null, index) : null;
    });
  }

  canSave() {
    return (
      this.state.conditions.size > 0 && this.state.conditions.every(context => context.ready())
    );
  }

  saveTriggerClick() {
    this.setState({saving: true});
    this.props.saveTriggerClick(
      new CallTrigger({
        conditions: new CallConditionAll({matchAll: this.state.conditions}),
      })
    );
  }

  renderInitialOptions() {
    return (
      <div className="rule-row empty-row call-trigger-rule-row">
        <label>When a call</label>
        <div className="rule-definition">
          <div className="call-option">
            <div className="rule-context">{this.getInitialOptions()}</div>
          </div>
        </div>
      </div>
    );
  }

  renderContinuingOptions(includeUser) {
    if (!this.needContinuingOptions()) {
      // If we are adding an "or" condition to an existing condition, we don't need to show the continuing options
      // this is to avoid a dangling "and" (see just a few lines below) in the wizard
      // that has no corresponding "plus condition" buttons
      return null;
    }
    return (
      <div className="rule-row empty-row">
        <label>
          <span>and</span>
        </label>
        <div className="rule-definition">
          <div className="call-option">
            <div className="rule-context">{this.getContinuingOptions(includeUser)}</div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {conditions, saving} = this.state;
    const userCondition = this.getUserCondition(conditions);
    const canSave = this.canSave() && !saving;

    return (
      <div className="create-call-rule">
        <p className="rule-title">What will trigger this rule?</p>
        <div>
          <div className="create-rule">
            {conditions.size === 0 && this.renderInitialOptions()}
            {conditions.size > 0 &&
              conditions.map((condition, i) => (
                <div className="rule-row call-trigger-rule-row" key={i}>
                  <label>{i > 0 ? <span>and</span> : <span>When a call</span>}</label>
                  <div className="rule-definition">
                    <div className="call-option">
                      <div className="rule-context">{this.getOption(condition, i)}</div>
                    </div>
                  </div>
                </div>
              ))}
            {conditions.size > 0 && this.renderContinuingOptions(!userCondition)}
          </div>
          <div className="button-container">
            <Button
              variant="contained"
              color="primary"
              className="ev-button"
              disabled={!canSave}
              onClick={this.saveTriggerClick}>
              Next
            </Button>
            <Button variant="outlined" className="ev-button" onClick={this.props.cancelClick}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
